import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useRouter } from 'common/utils/hooks';
import { ListItem, Drawer, Hidden, makeStyles, Theme } from '@material-ui/core';
import { Route } from 'common/router/routes';
import { shadows, zIndex } from 'common/styles/mixins';
import { deliveryCountSelector } from 'common/redux/global/selectors';

const SIDEBAR_COLLAPSED = 56;
const SIDEBAR_EXPANDED = 180;
const SIDEBAR_ICON = 32;
const BADGE_ACCENT = '#ff8315';

function isPathUnderRoute(pathname: string, routePath: string) {
  return pathname === routePath || pathname.startsWith(`${routePath}/`);
}

export interface SidebarIconProps {
  active: boolean;
  color: string;
  SvgComponent: React.FC<React.SVGProps<SVGSVGElement>>;
}

export interface MainSidebarProps {
  routes: Route[];
}

const useStyles = makeStyles<
  Theme,
  {
    visible?: boolean;
    expanded?: boolean;
  }
>(({ palette }) => ({
  toolbarSeparator: {
    minHeight: '50px',
    flexShrink: 0
  },
  drawer: {
    width: SIDEBAR_COLLAPSED,
    flexShrink: 0,
    overflow: 'visible',
    zIndex: zIndex.sidebar,
    boxShadow: 'none',
    '& .MuiPaper-root': {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: ({ expanded }) => (expanded ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED),
      backgroundColor: ({ visible }) => {
        if (!visible) {
          return palette.B['900'];
        }
        return '#04273F';
      },
      overflowX: 'hidden',
      whiteSpace: 'nowrap',
      borderRight: 'none',
      boxShadow: shadows[1],
      transition: 'width 0.25s ease',
      display: 'flex',
      flexDirection: 'column',
      zIndex: 9500
    }
  },
  navList: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0
  },
  listItem: {
    height: '72px',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 0,
    padding: '0 16px',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.06)'
    },
    '&.Mui-focusVisible, &:focus, &:active': {
      backgroundColor: 'transparent'
    }
  },
  listItemActive: {
    backgroundColor: `${palette.B[700]} !important`,
    '&:hover': {
      backgroundColor: `${palette.B[700]} !important`
    }
  },
  iconSlot: {
    // Pin icon to collapsed-rail center so expand only reveals labels.
    position: 'relative',
    flexShrink: 0,
    width: SIDEBAR_ICON,
    height: SIDEBAR_ICON,
    marginLeft: (SIDEBAR_COLLAPSED - SIDEBAR_ICON) / 2 - 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#ffffff',
    '& svg': {
      // No forzar fill global: los íconos fullline usan stroke + fill="none".
      color: '#ffffff',
      opacity: 1
    }
  },
  countBadge: {
    position: 'absolute',
    top: -6,
    right: -10,
    zIndex: 1,
    minWidth: 18,
    height: 18,
    padding: '0 5px',
    boxSizing: 'border-box' as const,
    borderRadius: 999,
    backgroundColor: BADGE_ACCENT,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    lineHeight: '18px',
    textAlign: 'center' as const,
    pointerEvents: 'none' as const
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: 600,
    color: 'rgba(255, 255, 255, 0.9)',
    whiteSpace: 'nowrap',
    letterSpacing: '0.01em',
    overflow: 'hidden',
    marginLeft: ({ expanded }) => (expanded ? 16 : 0),
    maxWidth: ({ expanded }) => (expanded ? 160 : 0),
    opacity: ({ expanded }) => (expanded ? 1 : 0),
    transform: ({ expanded }) => (expanded ? 'translateX(0)' : 'translateX(-5px)'),
    transition: ({ expanded }) =>
      expanded
        ? 'max-width 0.25s ease, opacity 0.18s ease 0.12s, transform 0.18s ease 0.12s, margin-left 0.25s ease'
        : 'opacity 0.12s ease, transform 0.12s ease, max-width 0.2s ease 0.08s, margin-left 0.2s ease'
  },
  userActionsContainer: {
    marginTop: 'auto',
    zIndex: zIndex.sidebar
  }
}));

function SidebarIcon({ SvgComponent, color }: SidebarIconProps) {
  // SCI: íconos del rail siempre blancos y opacos (sin atenuar inactivos).
  return (
    <SvgComponent
      width={SIDEBAR_ICON}
      height={SIDEBAR_ICON}
      style={{ opacity: 1, color }}
      aria-hidden
    />
  );
}

function formatDeliveriesBadge(count: number) {
  if (count > 99) {
    return '+99';
  }
  return String(count);
}

function SidebarItem({
  route,
  isActive,
  expanded,
  badgeCount
}: {
  route: Route;
  isActive: boolean;
  expanded: boolean;
  badgeCount?: number;
}) {
  const { activeIcon, icon, label } = route;
  const classes = useStyles({ expanded });
  const { push } = useRouter();
  const goTo = () => push(route.path);
  const showBadge = Number(badgeCount) > 0;

  return (
    <ListItem
      className={`${classes.listItem}${isActive ? ` ${classes.listItemActive}` : ''}`}
      onClick={goTo}
      button
      selected={false}
      key={label}
      title={expanded ? undefined : label}
    >
      <span className={classes.iconSlot}>
        <SidebarIcon
          color="#ffffff"
          // @ts-ignore
          SvgComponent={isActive ? activeIcon : icon}
          active={isActive}
        />
        {showBadge ? (
          <span className={classes.countBadge}>{formatDeliveriesBadge(Number(badgeCount))}</span>
        ) : null}
      </span>
      <span className={classes.label}>{label}</span>
    </ListItem>
  );
}

export function MainSidebar({ routes }: MainSidebarProps) {
  const location = useLocation();
  const deliveriesCount = useSelector(deliveryCountSelector);
  const isHome = location !== undefined && location.pathname === '/';
  const visible = !isHome;
  const [expanded, setExpanded] = useState(false);
  const leaveTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const classes = useStyles({ visible, expanded });

  // Home (welcome): full-bleed sin barra, dark y light.
  if (isHome) {
    return null;
  }

  const onMouseEnter = () => {
    if (leaveTimer.current) {
      clearTimeout(leaveTimer.current);
      leaveTimer.current = null;
    }
    if (visible) {
      setExpanded(true);
    }
  };

  const onMouseLeave = () => {
    leaveTimer.current = setTimeout(() => setExpanded(false), 120);
  };

  return (
    <>
      <Hidden xsDown>
        <Drawer
          className={`${classes.drawer} suppliers-main-sidebar`}
          variant="permanent"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <div className={classes.toolbarSeparator} />
          {visible && (
            <div className={classes.navList}>
              {(() => {
                const pathname = location.pathname;
                // @ts-ignore
                const { from } = location.state || {};
                // Solo una sección activa: match por pathname; `from` solo si no hay match
                // (ej. /order/:id vino de /summary).
                const matchedPath = routes.find(r => isPathUnderRoute(pathname, r.path))?.path;
                const activePath = matchedPath || (typeof from === 'string' ? from : undefined);

                return routes.map(route => {
                  const { path } = route;
                  const isActive = activePath === path;
                  const badgeCount = path === '/summary' ? Number(deliveriesCount) || 0 : undefined;

                  return (
                    <SidebarItem
                      {...{ route, isActive, expanded }}
                      badgeCount={badgeCount}
                      key={path}
                    />
                  );
                });
              })()}
            </div>
          )}
          <div className={classes.userActionsContainer}></div>
        </Drawer>
      </Hidden>
    </>
  );
}
